import { looksLikeConnection } from './outbox';
import { saveFailed, clearSaveFailed } from './saveFailed';

/*
  Two small helpers, and between them they are what "Stop the silent failures"
  is actually about.

  Written 5 September 2026.

  **The fault this job exists for.** When the database refuses a write, the app
  asks, throws the answer away without looking at it, and updates the screen as
  though it had worked. The person is told "Saved". Nothing was stored. That is
  how eleven notification writes, three shelf-position writes, every
  mark-as-read and a whole user import went wrong for months without anybody
  noticing: **none of them failed loudly, so none of them looked like a fault.**

  There is no clever fix for that. There is only a habit, and a habit needs a
  thing to reach for.
*/

/*
  Do a write and say so when it is refused.

  Use it anywhere the app writes and then tells somebody it worked:

    if (!await save(toast, () => supabase.from('x').update(...).eq('id', id), 'the note')) return;

  It answers true when the write went through and false when it did not, so the
  caller can stop rather than carrying on and reporting success.
*/
export async function save(toast, query, what) {
  /*
    **`query` is a function that MAKES the write, not the write itself**, and
    that changed on 10 September 2026 when "Try again" arrived.

    A Supabase query builder can only be awaited once meaningfully: it keeps the
    answer it got and hands the same one back. So a Try again button that
    re-awaited the same object would have reported the same failure for ever,
    while looking exactly like a button that works. **A button that cannot work
    is worse than no button**, because it tells somebody there is something they
    can do.

    A plain builder is still accepted, because it is what every caller passed
    until that day and a helper that starts throwing is a helper nobody reaches
    for. It simply cannot be retried, so it gets the message rather than the bar.
  */
  const makes = typeof query === 'function';
  let error = null;
  try {
    ({ error } = await (makes ? query() : query));
  } catch (e) {
    /* **A dropped connection THROWS rather than answering**, which this helper
       did not allow for until 10 September 2026. Every caller was inside an
       `await` with nothing round it, so a save attempted with no signal took the
       whole screen down instead of saying anything. */
    error = e;
  }
  if (!error) {
    clearSaveFailed();
    return true;
  }

  /*
    **A dropped signal and a refusal are different things and get different
    answers.** Added 10 September 2026 for job 25, the owner's own decision:
    count entry keeps working with no signal, every other screen just says so
    loudly and lets the person press it again.

    **The signal**: a bar that stays, with the button on it, because this is
    something the person has to DO and a message that goes on its own after five
    seconds takes the thing they have to do away with it.

    **A refusal**: the message it always was. Pressing Try again on a write the
    database will refuse for ever is a button that cannot work, and offering it
    is worse than not offering it.
  */
  if (looksLikeConnection(error) && makes) {
    saveFailed(() => save(toast, query, what), what);
    return false;
  }
  if (toast?.error) toast.error('Could not save ' + what + ': ' + error.message);
  return false;
}

/*
  **Do a write that has to change something, and say so when it changed
  nothing.** Added 18 September 2026, in the audit after the rebrand.

  `save` above catches a write the database REFUSES. It cannot catch the quieter
  one this project keeps meeting: a write that matches no row, or that a rule
  quietly filters to nothing, comes back with no error at all and has changed
  nothing. The Review Hub's Send back did exactly that from the day the Hub
  became a page: it aimed at a task id that was really a sheet id, updated no
  sheet, and told the admin "Count rejected. The rep has been notified."

  The query must end in `.select(...)` so the rows it changed come back:

    if (!await saveChanged(toast,
      () => supabase.from('x').update(...).eq('id', id).select('id'), 'the note')) return;
*/
export async function saveChanged(toast, query, what) {
  let rows = null;
  const ok = await save(toast, async () => {
    const answer = await query();
    rows = answer ? answer.data : null;
    return answer;
  }, what);
  if (!ok) return false;
  if (!rows || (Array.isArray(rows) && rows.length === 0)) {
    if (toast?.error) toast.error('Could not save ' + what + ': nothing was changed. It may have been dealt with already, or this account may not be allowed to change it.');
    return false;
  }
  return true;
}

/*
  Read every row, not the first thousand.

  **The database hands back about a thousand rows to one request** and says
  nothing about the ones it left behind. Three exports and the catalog export
  were written as though it hands back everything, so a file could be quietly
  short and carry a confident row count on the front of it. The open cycle holds
  3,250 count lines and the catalog holds 3,012 items, so all four were already
  over the line.

  It takes a function that BUILDS the query rather than a query, because a query
  can only be asked once and this asks repeatedly.

    const { data, error } = await fetchAll(() => supabase.from('x').select('*'));
*/
/*
  `max` is a ceiling, added 6 September 2026 for the audit log, which has no
  natural end. A screen that sets one must SAY on the screen when it is reached,
  or it has reinvented the fault this function exists to fix: a confident total
  printed over a quietly short answer.
*/
export async function fetchAll(makeQuery, { pageSize = 1000, max = Infinity } = {}) {
  const all = [];
  for (let from = 0; from < max; from += pageSize) {
    const take = Math.min(pageSize, max - from);
    const { data, error } = await makeQuery().range(from, from + take - 1);
    if (error) return { data: null, error };
    all.push(...(data || []));
    if (!data || data.length < take) return { data: all, error: null };
  }
  return { data: all, error: null };
}
